import { Void } from '@acme/common'
import React, { createContext } from 'react'
import { PopoverPosition } from '../utils'

interface MenuRootProps extends Omit<React.ComponentProps<'div'>, 'position'> {
    position?: PopoverPosition
    arrow?: boolean
    open?: boolean
    onOpenChange?: (open: boolean) => void
    /** Abre o menu automaticamente ao montar, ancorado no próprio container. */
    defaultOpen?: boolean
}

type EventContext = React.MouseEvent<any>

interface MenuContextProps {
    id: string
    open: boolean
    anchorEl: HTMLElement | null
    handleClose: (event: EventContext) => void
    handleClick: (event: EventContext) => void
    position: PopoverPosition
    arrow?: boolean
}

export const MenuContext = createContext<MenuContextProps>({
    id: 'MenuRoot',
    open: false,
    anchorEl: null,
    handleClose: Void,
    handleClick: Void,
    position: 'center',
    arrow: false,
})

export default function MenuRoot({
    children,
    id,
    position = 'center',
    arrow,
    open: openProp,
    onOpenChange,
    defaultOpen,
    ...rest
}: MenuRootProps) {
    const boxRef = React.useRef<HTMLDivElement>(null)
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null)
    const isControlled = openProp !== undefined
    const open = isControlled ? openProp : Boolean(anchorEl)

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget)
        onOpenChange?.(true)
    }
    const handleClose = () => {
        if (!isControlled) setAnchorEl(null)
        onOpenChange?.(false)
    }

    React.useEffect(() => {
        if (defaultOpen && boxRef.current) setAnchorEl(boxRef.current)
        if (isControlled && openProp && !anchorEl && boxRef.current) {
            setAnchorEl(boxRef.current)
        }
    }, [defaultOpen, isControlled, openProp, anchorEl])

    return (
        <MenuContext.Provider
            value={{
                open,
                anchorEl,
                handleClose,
                handleClick,
                id: id || 'MenuRoot',
                position,
                arrow,
            }}
        >
            <div ref={boxRef} id={id || 'MenuRoot'} {...rest}>
                {children}
            </div>
        </MenuContext.Provider>
    )
}
